import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { AlertCircle, CheckCircle, XCircle, Clock, Calendar, User, Package, FileText, Eye, ArrowLeft, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { encryptId } from '../utils/urlEncryption';
import RevisionDetailCard from '../components/RevisionDetailCard';

const AdminApprovals = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingData, setPendingData] = useState({
    orders: [],
    bookings: [],
    total_count: 0
  });
  const [revisionData, setRevisionData] = useState({
    orders: [],
    bookings: [],
    total_count: 0
  });
  const [provisionalData, setProvisionalData] = useState({
    orders: [],
    total_count: 0
  });
  // New: Pending Approvals (the module)
  const [pendingApprovalsList, setPendingApprovalsList] = useState([]);
  const [provisionalApprovalsList, setProvisionalApprovalsList] = useState([]);
  
  const [activeTab, setActiveTab] = useState('orders');
  const [provisionalTab, setProvisionalTab] = useState('orders'); // New state for provisional section
  const [viewMode, setViewMode] = useState('new_orders'); // 'new_orders' or 'provisional_changes'
  const [rejectReason, setRejectReason] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const [showActionsMenu, setShowActionsMenu] = useState({});

  const toggleActionsMenu = (entityId) => {
    setShowActionsMenu(prev => ({
      ...prev,
      [entityId]: !prev[entityId]
    }));
  };

  const closeAllMenus = () => {
    setShowActionsMenu({});
  };

  useEffect(() => {
    loadPendingApprovals();
    loadPendingRevisions();
    loadProvisionalOrders();
    loadPendingApprovalsList();
    loadProvisionalApprovalsList();
  }, []);

  // Helper functions for the Force Remove functionality
  const getEntityTypeForTab = (tab) => {
    switch (tab) {
      case 'orders': return 'order';
      case 'bookings': return 'booking';
      case 'approvals': return 'approval';
      case 'kitty': return 'kitty_enrollment';
      case 'stock_orders': return 'stock_order';
      case 'repairs': return 'repair';
      default: return tab;
    }
  };

  const getEntityId = (entity) => {
    return entity.order_id || entity.booking_id || entity.approval_id || 
           entity.enrollment_id || entity.stock_order_id || entity.repair_id;
  };

  const navigateToDetail = (tab, entity) => {
    const entityId = getEntityId(entity);
    const encryptedId = encryptId(entityId);
    
    switch (tab) {
      case 'orders':
        navigate(`/orders/${encryptedId}?from=pending`);
        break;
      case 'bookings':
        navigate(`/bookings/${encryptedId}?from=pending`);
        break;
      case 'approvals':
        navigate(`/approvals/${encryptedId}?from=pending`);
        break;
      case 'kitty':
        navigate(`/kitty/enrollments/${encryptedId}`);
        break;
      case 'stock_orders':
        navigate(`/stock-orders/${entityId}`);
        break;
      case 'repairs':
        navigate(`/repairs/${entityId}`);
        break;
      default:
        console.warn('Unknown tab type:', tab);
    }
  };

  const loadPendingApprovals = async () => {
    try {
      setLoading(true);
      // Add timestamp to prevent caching
      const response = await axiosInstance.get(`/admin/pending-approvals?_t=${Date.now()}`);
      setPendingData(response.data);
    } catch (error) {
      console.error('Error loading pending approvals:', error);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRevisions = async () => {
    try {
      const response = await axiosInstance.get('/admin/pending-revisions');
      setRevisionData(response.data);
    } catch (error) {
      console.error('Error loading pending revisions:', error);
      toast.error('Failed to load pending revisions');
    }
  };

  const loadProvisionalOrders = async () => {
    try {
      const response = await axiosInstance.get('/admin/provisional-orders');
      setProvisionalData(response.data);
    } catch (error) {
      console.error('Error loading provisional orders:', error);
    }
  };

  // Load pending Approvals (the module)
  const loadPendingApprovalsList = async () => {
    try {
      const response = await axiosInstance.get('/admin/pending-approval-requests');
      // This endpoint returns pending approvals for the Approval module
      if (Array.isArray(response.data)) {
        setPendingApprovalsList(response.data);
      }
    } catch (error) {
      console.error('Error loading pending approvals list:', error);
    }
  };

  const loadProvisionalApprovalsList = async () => {
    try {
      const response = await axiosInstance.get('/admin/provisional-approvals');
      if (Array.isArray(response.data)) {
        setProvisionalApprovalsList(response.data);
      }
    } catch (error) {
      console.error('Error loading provisional approvals:', error);
    }
  };

  // Handle approve/reject for Approval module
  const handleApproveApproval = async (approvalId) => {
    try {
      await axiosInstance.post(`/approvals/${approvalId}/approve`);
      toast.success('Approval approved successfully!');
      loadPendingApprovalsList();
    } catch (error) {
      console.error('Error approving approval:', error);
      toast.error('Failed to approve');
    }
  };

  const handleRejectApproval = async (approvalId, reason) => {
    try {
      await axiosInstance.post(`/approvals/${approvalId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Approval rejected');
      loadPendingApprovalsList();
    } catch (error) {
      console.error('Error rejecting approval:', error);
      toast.error('Failed to reject');
    }
  };

  const handleApproveApprovalChanges = async (approvalId) => {
    try {
      await axiosInstance.post(`/approvals/${approvalId}/approve-changes`);
      toast.success('Provisional changes approved!');
      loadProvisionalApprovalsList();
    } catch (error) {
      console.error('Error approving changes:', error);
      toast.error('Failed to approve changes');
    }
  };

  const handleRejectApprovalChanges = async (approvalId) => {
    try {
      await axiosInstance.post(`/approvals/${approvalId}/reject-changes`);
      toast.success('Changes rejected and rolled back');
      loadProvisionalApprovalsList();
    } catch (error) {
      console.error('Error rejecting changes:', error);
      toast.error('Failed to reject changes');
    }
  };

  const handleApprove = async (entityType, entityId) => {
    try {
      await axiosInstance.post(`/admin/approve/${entityType}/${entityId}`);
      toast.success(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} approved successfully!`);
      loadPendingApprovals();
    } catch (error) {
      console.error('Error approving:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to approve';
      toast.error(errorMsg);
    }
  };

  const handleApproveProvisional = async (orderId) => {
    try {
      await axiosInstance.post(`/admin/approve-provisional/${orderId}`);
      toast.success('Provisional changes approved successfully!');
      loadProvisionalOrders();
    } catch (error) {
      console.error('Error approving provisional changes:', error);
      toast.error('Failed to approve provisional changes');
    }
  };

  const handleRollbackProvisional = async () => {
    try {
      const { orderId } = selectedEntity;
      await axiosInstance.post(`/admin/rollback-provisional/${orderId}`);
      toast.success('Provisional changes rolled back');
      setShowRejectModal(false);
      setSelectedEntity(null);
      loadProvisionalOrders();
    } catch (error) {
      console.error('Error rolling back:', error);
      toast.error('Failed to rollback changes');
    }
  };

  const handleReject = async () => {
    try {
      const { entityType, entityId } = selectedEntity;
      await axiosInstance.post(`/admin/reject/${entityType}/${entityId}`);
      toast.success(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} rejected`);
      setShowRejectModal(false);
      setSelectedEntity(null);
      loadPendingApprovals();
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error('Failed to reject');
    }
  };

  const openRejectModal = (entityType, entityId) => {
    setSelectedEntity({ entityType, entityId });
    setShowRejectModal(true);
  };

  // Revision approval handlers
  const handleRevisionApprove = async (entityType, entityId) => {
    try {
      await axiosInstance.post(`/admin/approve-revision/${entityType}/${entityId}`);
      toast.success('Changes approved successfully!');
      loadPendingRevisions();
      loadPendingApprovals(); // Reload main list too in case status changed
    } catch (error) {
      console.error('Error approving revision:', error);
      toast.error('Failed to approve changes');
    }
  };

  const handleRevisionReject = async (entityType, entityId) => {
    try {
      await axiosInstance.post(`/admin/reject-revision/${entityType}/${entityId}`, { reason: 'Rejected by admin' });
      toast.success('Changes rejected');
      loadPendingRevisions();
      loadPendingApprovals();
    } catch (error) {
      console.error('Error rejecting revision:', error);
      toast.error('Failed to reject changes');
    }
  };

  const getDaysLabel = (item) => {
    if (item.is_backdated) {
      return (
        <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-bold">
          ⚠️ BACKDATED {item.days_difference} {item.days_difference === 1 ? 'DAY' : 'DAYS'}
        </span>
      );
    } else if (item.is_future_dated) {
      return (
        <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-bold">
          📅 FUTURE {item.days_difference} {item.days_difference === 1 ? 'DAY' : 'DAYS'}
        </span>
      );
    } else {
      return (
        <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-bold">
          ✓ CURRENT DATE
        </span>
      );
    }
  };


  // Filter function for search
  const filterItems = (items) => {
    if (!searchQuery.trim()) return items;
    
    const query = searchQuery.toLowerCase();
    return items.filter(item => {
      const customerName = (item.customer_name || '').toLowerCase();
      const orderId = (item.order_id || item.booking_id || '').toLowerCase();
      const customerId = (item.customer_id || '').toLowerCase();
      const createdBy = (item.created_by || '').toLowerCase();
      
      return (
        customerName.includes(query) ||
        orderId.includes(query) ||
        customerId.includes(query) ||
        createdBy.includes(query)
      );
    });
  };


  const renderOrderCard = (order) => (
    <div key={order.order_id} className="bg-white border-2 border-amber-200 rounded-xl p-5 hover:shadow-xl transition-all">
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-800">{order.order_id}</h3>
          <p className="text-xs text-gray-500">By: {order.created_by}</p>
        </div>
        {getDaysLabel(order)}
      </div>

      {/* Customer Info */}
      <div className="bg-blue-50 rounded-lg p-3 mb-3">
        <p className="text-sm font-semibold text-gray-700">Customer: {order.customer_name || order.customer_id}</p>
        <p className="text-xs text-gray-600">Date: {
          (() => {
            const d = order.order_date || order.created_at;
            if (!d) return 'N/A';
            const dt = new Date(d);
            return isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleDateString('en-IN');
          })()
        }</p>
        <p className="text-xs text-gray-600">Items: {order.items?.length || 0}</p>
      </div>

      {/* Reason if provided */}
      {order.staff_reason && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
          <p className="text-xs text-gray-700"><span className="font-semibold">Reason:</span> {order.staff_reason}</p>
        </div>
      )}

      {/* Actions - Optimized Layout */}
      <div className="flex gap-2">
        <button
          onClick={() => navigate(`/orders/${encryptId(order.order_id)}?from=pending`)}
          className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
          title="View details"
        >
          <Eye className="w-5 h-5" />
        </button>
        <button
          onClick={() => handleApprove('order', order.order_id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold"
        >
          <CheckCircle className="w-4 h-4" />
          Approve
        </button>
        <button
          onClick={() => openRejectModal('order', order.order_id)}
          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          title="Reject"
        >
          <XCircle className="w-5 h-5" />
        </button>
        <div className="relative">
          <button
            onClick={() => toggleActionsMenu(order.order_id)}
            className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            title="More actions"
          >
            ⋮
          </button>
          {showActionsMenu[order.order_id] && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => closeAllMenus()}
              />
              <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-20">
                <button
                  onClick={async () => {
                    closeAllMenus();
                    if (window.confirm(`Force remove ${order.order_id} from pending list?\n\nOnly use this if the item is stuck and already approved.`)) {
                      try {
                        await axiosInstance.post(`/admin/force-remove-pending/order/${order.order_id}`);
                        toast.success('Item removed from pending list');
                        loadPendingApprovals();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to remove');
                      }
                    }
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 text-sm border-b"
                >
                  <span className="text-lg">🔧</span>
                  <span>Force Remove (if stuck)</span>
                </button>
                <button
                  onClick={async () => {
                    closeAllMenus();
                    if (window.confirm(`⚠️ PERMANENTLY DELETE ${order.order_id}?\n\nThis will HARD DELETE the record from database.\nOnly use if the item is already processed and stuck.\n\nAre you sure?`)) {
                      try {
                        await axiosInstance.delete(`/admin/hard-delete-pending/order/${order.order_id}`);
                        toast.success('Item permanently deleted');
                        loadPendingApprovals();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to delete');
                      }
                    }
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 text-sm text-red-600"
                >
                  <span className="text-lg">🗑️</span>
                  <span>Hard Delete</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderBookingCard = (booking) => (
    <div key={booking.booking_id} className="bg-white border-2 border-purple-200 rounded-xl p-5 hover:shadow-xl transition-all">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-800">{booking.booking_id}</h3>
          <p className="text-xs text-gray-500">By: {booking.created_by}</p>
        </div>
        {getDaysLabel(booking)}
      </div>

      <div className="bg-purple-50 rounded-lg p-3 mb-3">
        <p className="text-sm font-semibold text-gray-700">Customer: {booking.customer_name || booking.customer_id}</p>
        <p className="text-xs text-gray-600">Date: {
          (() => {
            const d = booking.booking_date || booking.created_at;
            if (!d) return 'N/A';
            const dt = new Date(d);
            return isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleDateString('en-IN');
          })()
        }</p>
        <p className="text-xs text-gray-600">Items: {booking.items?.length || 0}</p>
      </div>

      {booking.staff_reason && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
          <p className="text-xs text-gray-700"><span className="font-semibold">Reason:</span> {booking.staff_reason}</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => navigate(`/bookings/${encryptId(booking.booking_id)}?from=pending`)}
          className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
          title="View details"
        >
          <Eye className="w-5 h-5" />
        </button>
        <button
          onClick={() => handleApprove('booking', booking.booking_id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold"
        >
          <CheckCircle className="w-4 h-4" />
          Approve
        </button>
        <button
          onClick={() => openRejectModal('booking', booking.booking_id)}
          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          title="Reject"
        >
          <XCircle className="w-5 h-5" />
        </button>
        <div className="relative">
          <button
            onClick={() => toggleActionsMenu(booking.booking_id)}
            className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            title="More actions"
          >
            ⋮
          </button>
          {showActionsMenu[booking.booking_id] && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => closeAllMenus()}
              />
              <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-20">
                <button
                  onClick={async () => {
                    closeAllMenus();
                    if (window.confirm(`Force remove ${booking.booking_id} from pending list?\n\nOnly use this if the item is stuck and already approved.`)) {
                      try {
                        await axiosInstance.post(`/admin/force-remove-pending/booking/${booking.booking_id}`);
                        toast.success('Item removed from pending list');
                        loadPendingApprovals();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to remove');
                      }
                    }
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 text-sm border-b"
                >
                  <span className="text-lg">🔧</span>
                  <span>Force Remove (if stuck)</span>
                </button>
                <button
                  onClick={async () => {
                    closeAllMenus();
                    if (window.confirm(`⚠️ PERMANENTLY DELETE ${booking.booking_id}?\n\nThis will HARD DELETE the record from database.\nOnly use if the item is already processed and stuck.\n\nAre you sure?`)) {
                      try {
                        await axiosInstance.delete(`/admin/hard-delete-pending/booking/${booking.booking_id}`);
                        toast.success('Booking permanently deleted');
                        loadPendingApprovals();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to delete');
                      }
                    }
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 text-sm text-red-600"
                >
                  <span className="text-lg">🗑️</span>
                  <span>Hard Delete</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderProvisionalOrderCard = (order) => (
    <div key={order.order_id} className="bg-white border-2 border-orange-200 rounded-xl p-5 hover:shadow-xl transition-all">
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-800">{order.order_id}</h3>
          <p className="text-xs text-gray-500">Modified by: {order.provisional_by}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-bold">
            ⏳ PENDING REVIEW
          </span>
          <p className="text-xs text-gray-500">
            {new Date(order.provisional_since).toLocaleDateString('en-IN')}
          </p>
        </div>
      </div>

      {/* Customer Info */}
      <div className="bg-orange-50 rounded-lg p-3 mb-3">
        <p className="text-sm font-semibold text-gray-700">Customer: {order.customer_name || order.customer_id}</p>
        <p className="text-xs text-gray-600">Items: {order.items?.length || 0} | Total: ₹{order.total_amount?.toLocaleString('en-IN') || 0}</p>
      </div>

      {/* Provisional Changes Note */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
        <p className="text-xs text-gray-700"><span className="font-semibold">Note:</span> Provisional changes pending review</p>
      </div>

      {/* Actions - Optimized Layout */}
      <div className="flex gap-2">
        <button
          onClick={() => navigate(`/orders/${encryptId(order.order_id)}?from=provisional`)}
          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          title="View details"
        >
          <Eye className="w-5 h-5" />
        </button>
        <button
          onClick={() => handleApproveProvisional(order.order_id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold"
        >
          <CheckCircle className="w-4 h-4" />
          Approve Changes
        </button>
        <button
          onClick={() => {
            setSelectedEntity({ orderId: order.order_id, type: 'provisional' });
            setShowRejectModal(true);
          }}
          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          title="Rollback changes"
        >
          <XCircle className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  const renderStockOrderCard = (stockOrder) => (
    <div key={stockOrder.stock_order_id} className="bg-white border-2 border-indigo-200 rounded-xl p-5 hover:shadow-xl transition-all">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-800">{stockOrder.stock_order_id}</h3>
          <p className="text-xs text-gray-500">By: {stockOrder.created_by}</p>
        </div>
        {getDaysLabel(stockOrder)}
      </div>

      <div className="bg-indigo-50 rounded-lg p-3 mb-3">
        <p className="text-sm font-semibold text-gray-700">Staff: {stockOrder.staff_name}</p>
        <p className="text-xs text-gray-600">Date: {
          (() => {
            const d = stockOrder.order_date || stockOrder.created_at;
            if (!d) return 'N/A';
            const dt = new Date(d);
            return isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleDateString('en-IN');
          })()
        }</p>
        <p className="text-xs text-gray-600">Items: {stockOrder.items?.length || 0}</p>
      </div>

      {stockOrder.staff_reason && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
          <p className="text-xs text-gray-700"><span className="font-semibold">Reason:</span> {stockOrder.staff_reason}</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => navigate(`/stock-orders/${stockOrder.stock_order_id}`)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-semibold"
        >
          <Eye className="w-4 h-4" />
          View
        </button>
        <button
          onClick={() => handleApprove('stock_order', stockOrder.stock_order_id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold"
        >
          <CheckCircle className="w-4 h-4" />
          Approve
        </button>
        <button
          onClick={() => openRejectModal('stock_order', stockOrder.stock_order_id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold"
        >
          <XCircle className="w-4 h-4" />
          Reject
        </button>
      </div>
    </div>
  );

  const renderRepairCard = (repair) => (
    <div key={repair.repair_id} className="bg-white border-2 border-green-200 rounded-xl p-5 hover:shadow-xl transition-all">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-800">{repair.repair_id}</h3>
          <p className="text-xs text-gray-500">By: {repair.created_by}</p>
        </div>
        {getDaysLabel(repair)}
      </div>

      <div className="bg-green-50 rounded-lg p-3 mb-3">
        <p className="text-sm font-semibold text-gray-700">Customer: {repair.customer_id}</p>
        <p className="text-xs text-gray-600">Item: {repair.item_type}</p>
        <p className="text-xs text-gray-600">Date: {repair.intake_date ? new Date(repair.intake_date).toLocaleDateString('en-IN') : 'N/A'}</p>
      </div>

      {repair.staff_reason && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
          <p className="text-xs text-gray-700"><span className="font-semibold">Reason:</span> {repair.staff_reason}</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => navigate(`/repairs/${repair.repair_id}`)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-semibold"
        >
          <Eye className="w-4 h-4" />
          View
        </button>
        <button
          onClick={() => handleApprove('repair', repair.repair_id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold"
        >
          <CheckCircle className="w-4 h-4" />
          Approve
        </button>
        <button
          onClick={() => openRejectModal('repair', repair.repair_id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold"
        >
          <XCircle className="w-4 h-4" />
          Reject
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6">
        {/* Header with Back Button */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">Pending Approvals</h1>
              <p className="text-gray-600 text-xs sm:text-sm mt-1">Review and approve backdated/future-dated submissions</p>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by customer name, order ID, booking ID..."
              className="w-full pl-10 pr-10 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Total Count Badges */}
        <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-6 flex-wrap">
          {pendingData.total_count > 0 && (
            <div className="bg-red-500 text-white px-3 sm:px-4 py-2 rounded-lg inline-block font-bold text-xs sm:text-sm">
              {pendingData.total_count} New Pending
            </div>
          )}
          {provisionalData.total_count > 0 && (
            <div className="bg-orange-500 text-white px-3 sm:px-4 py-2 rounded-lg inline-block font-bold text-xs sm:text-sm">
              {provisionalData.total_count} Provisional
            </div>
          )}
          {revisionData.total_count > 0 && (
            <div className="bg-yellow-500 text-white px-3 sm:px-4 py-2 rounded-lg inline-block font-bold text-xs sm:text-sm">
              {revisionData.total_count} Old Revision
            </div>
          )}
        </div>

        {/* View Mode Toggle - Mobile Optimized */}
        <div className="bg-white border-2 border-gray-200 rounded-xl p-1.5 sm:p-2 mb-4 sm:mb-6 inline-flex gap-1 sm:gap-2 w-full sm:w-auto">
          <button
            onClick={() => setViewMode('new_orders')}
            className={`flex-1 sm:flex-none px-3 sm:px-6 py-2 rounded-lg font-semibold transition-all text-xs sm:text-sm ${
              viewMode === 'new_orders'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            📝 New Orders
          </button>
          <button
            onClick={() => setViewMode('provisional_changes')}
            className={`px-6 py-2 rounded-lg font-semibold transition-all ${
              viewMode === 'provisional_changes'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            ⏳ Provisional Changes {provisionalData.total_count > 0 && `(${provisionalData.total_count})`}
          </button>
        </div>

        {/* Tabs - Only show for new orders view */}
        {viewMode === 'new_orders' && (
          <div className="flex gap-2 mb-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap ${
                activeTab === 'orders'
                  ? 'bg-amber-500 text-white'
                  : 'bg-white text-gray-700 border-2 border-amber-200'
              }`}
            >
              Orders ({pendingData.orders?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('bookings')}
              className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap ${
                activeTab === 'bookings'
                  ? 'bg-purple-500 text-white'
                  : 'bg-white text-gray-700 border-2 border-purple-200'
              }`}
            >
              Bookings ({pendingData.bookings?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('approvals')}
              className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap ${
                activeTab === 'approvals'
                  ? 'bg-green-500 text-white'
                  : 'bg-white text-gray-700 border-2 border-green-200'
              }`}
            >
              Approvals ({pendingApprovalsList?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('kitty')}
              className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap ${
                activeTab === 'kitty'
                  ? 'bg-pink-500 text-white'
                  : 'bg-white text-gray-700 border-2 border-pink-200'
              }`}
            >
              Kitty ({pendingData.kitty_enrollments?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('revisions')}
              className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap ${
                activeTab === 'revisions'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-white text-gray-700 border-2 border-yellow-200'
              }`}
            >
              Old Revisions ({revisionData.total_count || 0})
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading pending approvals...</p>
          </div>
        )}

        {/* Content */}
        {!loading && (
          <>
            {viewMode === 'provisional_changes' ? (
              /* Provisional Changes View */
              <div className="space-y-8">
                {/* Tab Navigation for Provisional */}
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                  <button
                    onClick={() => setProvisionalTab('orders')}
                    className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
                      provisionalTab === 'orders'
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Orders ({provisionalData.orders?.length || 0})
                  </button>
                  <button
                    onClick={() => setProvisionalTab('approvals')}
                    className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
                      provisionalTab === 'approvals'
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Approvals ({provisionalApprovalsList?.length || 0})
                  </button>
                  <button
                    onClick={() => setProvisionalTab('kitty')}
                    className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
                      provisionalTab === 'kitty'
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Kitty ({provisionalData.kitty_enrollments?.length || 0})
                  </button>
                </div>

                {/* Provisional Orders */}
                {provisionalTab === 'orders' && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      Orders with Provisional Changes ({provisionalData.orders?.length || 0})
                    </h2>
                    {(provisionalData.orders?.length || 0) > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filterItems(provisionalData.orders || []).map(renderProvisionalOrderCard)}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-white rounded-xl border-2 border-gray-200">
                        <p className="text-gray-500">No pending order changes</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Provisional Approvals */}
                {provisionalTab === 'approvals' && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      Approvals with Provisional Changes ({provisionalApprovalsList?.length || 0})
                    </h2>
                    {(provisionalApprovalsList?.length || 0) > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {provisionalApprovalsList.map(approval => (
                        <div key={approval.approval_id} className="bg-white rounded-xl shadow-md border-2 border-orange-300 overflow-hidden">
                          <div className="bg-gradient-to-r from-orange-100 to-amber-100 p-3 border-b">
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="font-bold text-orange-800">{approval.approval_id}</span>
                                <p className="text-xs text-gray-600 mt-1">Modified by: {approval.provisional_by}</p>
                              </div>
                              <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs font-bold rounded">
                                Provisional
                              </span>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm text-gray-600 mb-3">
                              Changed: {new Date(approval.provisional_since).toLocaleString('en-IN')}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApproveApprovalChanges(approval.approval_id)}
                                className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700"
                              >
                                ✓ Approve
                              </button>
                              <button
                                onClick={() => handleRejectApprovalChanges(approval.approval_id)}
                                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
                              >
                                ✗ Reject
                              </button>
                              <button
                                onClick={() => navigate(`/approvals/${encryptId(approval.approval_id)}?from=pending`)}
                                className="py-2 px-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-white rounded-xl border-2 border-gray-200">
                        <p className="text-gray-500">No pending approval changes</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Provisional Kitty Enrollments */}
                {provisionalTab === 'kitty' && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">
                      Kitty with Provisional Changes ({provisionalData.kitty_enrollments?.length || 0})
                    </h2>
                    {(provisionalData.kitty_enrollments?.length || 0) > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(provisionalData.kitty_enrollments || []).map(enrollment => (
                        <div key={enrollment.enrollment_id} className="bg-white rounded-xl shadow-md border-2 border-pink-300 overflow-hidden">
                          <div className="bg-gradient-to-r from-pink-100 to-rose-100 p-3 border-b">
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="font-bold text-pink-800">{enrollment.enrollment_id}</span>
                                <p className="text-xs text-gray-600 mt-1">Modified by: {enrollment.provisional_by}</p>
                              </div>
                              <span className="px-2 py-1 bg-pink-200 text-pink-800 text-xs font-bold rounded">
                                Provisional
                              </span>
                            </div>
                          </div>
                          <div className="p-4">
                            <p className="text-sm text-gray-700 mb-1">Customer: <span className="font-semibold">{enrollment.customer_id}</span></p>
                            <p className="text-sm text-gray-700 mb-1">Scheme: <span className="font-semibold">{enrollment.scheme_id}</span></p>
                            <p className="text-sm text-gray-600 mb-3">
                              Changed: {new Date(enrollment.provisional_since).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  try {
                                    await axiosInstance.post(`/admin/approve-provisional-kitty/${enrollment.enrollment_id}`);
                                    toast.success('Kitty changes approved');
                                    loadProvisionalOrders();
                                  } catch (err) {
                                    toast.error('Failed to approve');
                                  }
                                }}
                                className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700"
                              >
                                Approve
                              </button>
                              <button
                                onClick={async () => {
                                  const reason = window.prompt('Rejection reason:');
                                  if (!reason) return;
                                  try {
                                    await axiosInstance.post(`/admin/rollback-provisional-kitty/${enrollment.enrollment_id}`, { reason });
                                    toast.success('Kitty changes rejected');
                                    loadProvisionalOrders();
                                  } catch (err) {
                                    toast.error('Failed to reject');
                                  }
                                }}
                                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
                              >
                                Reject
                              </button>
                              <button
                                onClick={() => navigate(`/kitty/enrollments/${encryptId(enrollment.enrollment_id)}`)}
                                className="py-2 px-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-white rounded-xl border-2 border-gray-200">
                        <p className="text-gray-500">No pending kitty changes</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Empty State for Provisional Changes */}
                {provisionalTab === 'orders' && (provisionalData.orders?.length || 0) === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl border-2 border-gray-200">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg font-semibold">No Provisional Order Changes</p>
                    <p className="text-gray-500 text-sm mt-2">All order changes have been reviewed</p>
                  </div>
                )}
                {provisionalTab === 'approvals' && (provisionalApprovalsList?.length || 0) === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl border-2 border-gray-200">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg font-semibold">No Provisional Approval Changes</p>
                    <p className="text-gray-500 text-sm mt-2">All approval changes have been reviewed</p>
                  </div>
                )}
                {provisionalTab === 'kitty' && (provisionalData.kitty_enrollments?.length || 0) === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl border-2 border-gray-200">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg font-semibold">No Provisional Kitty Changes</p>
                    <p className="text-gray-500 text-sm mt-2">All kitty changes have been reviewed</p>
                  </div>
                )}
              </div>
            ) : (
              /* New Orders/Approvals View */
              <>
                {activeTab === 'revisions' ? (
                  <div className="space-y-4">
                    {(revisionData.orders || []).map(order => (
                      <RevisionDetailCard
                        key={order.order_id}
                        entity={order}
                        entityType="order"
                        onApprove={handleRevisionApprove}
                        onReject={handleRevisionReject}
                      />
                    ))}
                    {(revisionData.bookings || []).map(booking => (
                      <RevisionDetailCard
                        key={booking.booking_id}
                        entity={booking}
                        entityType="booking"
                        onApprove={handleRevisionApprove}
                        onReject={handleRevisionReject}
                      />
                    ))}
                    {(revisionData.kitty_enrollments || []).map(enrollment => (
                      <RevisionDetailCard
                        key={enrollment.enrollment_id}
                        entity={enrollment}
                        entityType="kitty_enrollment"
                        onApprove={handleRevisionApprove}
                        onReject={handleRevisionReject}
                      />
                    ))}
                  </div>
                ) : activeTab === 'approvals' ? (
                  /* Approvals Tab Content */
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(pendingApprovalsList || []).map(approval => (
                      <div key={approval.approval_id} className="bg-white rounded-xl shadow-md border-2 border-green-200 overflow-hidden hover:shadow-lg transition-shadow">
                        <div className="bg-gradient-to-r from-green-100 to-emerald-100 p-3 border-b">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-bold text-green-800">{approval.approval_id}</span>
                              <p className="text-xs text-gray-600 mt-1">
                                {(() => {
                                  const d = approval.order_date || approval.booking_date || approval.created_at;
                                  if (!d) return 'N/A';
                                  const dt = new Date(d);
                                  return isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleDateString('en-IN');
                                })()}
                              </p>
                            </div>
                            <span className="px-2 py-1 bg-yellow-200 text-yellow-800 text-xs font-bold rounded">
                              Pending
                            </span>
                          </div>
                        </div>
                        <div className="p-4">
                          <p className="text-xs text-gray-600">Items: {approval.items?.length || 0}</p>
                          <p className="text-xs text-gray-600">Created by: {approval.created_by || 'Unknown'}</p>
                          {approval.return_date && (
                            <p className="text-xs text-gray-600">Return: {new Date(approval.return_date).toLocaleDateString('en-IN')}</p>
                          )}
                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={() => handleApproveApproval(approval.approval_id)}
                              className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700"
                            >
                              ✓ Approve
                            </button>
                            <button
                              onClick={() => {
                                setSelectedEntity({ type: 'approval', id: approval.approval_id });
                                setShowRejectModal(true);
                              }}
                              className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
                            >
                              ✗ Reject
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Force remove this item from pending list? Use this only if the item is stuck and already approved.')) {
                                  try {
                                    await axiosInstance.post(`/admin/force-remove-pending/approval/${approval.approval_id}`);
                                    toast.success('Item removed from pending list');
                                    loadPendingApprovals();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Failed to remove item');
                                  }
                                }
                              }}
                              className="py-2 px-3 bg-orange-600 text-white rounded-lg text-sm font-semibold hover:bg-orange-700"
                              title="Force remove from pending list"
                            >
                              🔧 Fix
                            </button>
                            <button
                              onClick={() => navigate(`/approvals/${encryptId(approval.approval_id)}?from=provisional`)}
                              className="py-2 px-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                            >
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeTab === 'orders' && filterItems(pendingData.orders || []).map(renderOrderCard)}
                    {activeTab === 'bookings' && filterItems(pendingData.bookings || []).map(renderBookingCard)}
                    {activeTab === 'kitty' && filterItems(pendingData.kitty_enrollments || []).map(enrollment => (
                      <div key={enrollment.enrollment_id} className="bg-white border-2 border-pink-200 rounded-xl p-4 hover:shadow-lg transition-all">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-bold text-gray-800">{enrollment.enrollment_id}</h3>
                            <p className="text-xs text-gray-500">Customer: {enrollment.customer_id}</p>
                          </div>
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Pending
                          </span>
                        </div>
                        <div className="bg-pink-50 rounded-lg p-3 mb-3 space-y-1">
                          <p className="text-sm text-gray-700">Scheme: <span className="font-semibold">{enrollment.scheme_id}</span></p>
                          <p className="text-sm text-gray-700">Mode: <span className="font-semibold">{enrollment.payment_mode}</span></p>
                          {enrollment.amount_per_period && (
                            <p className="text-sm text-gray-700">Amount/Period: <span className="font-semibold">₹{enrollment.amount_per_period}</span></p>
                          )}
                          <p className="text-sm text-gray-700">Start: <span className="font-semibold">{enrollment.start_date}</span></p>
                          <p className="text-xs text-gray-500">Created by: {enrollment.created_by}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove('kitty_enrollment', enrollment.enrollment_id)}
                            className="flex-1 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              setSelectedEntity({ entityType: 'kitty_enrollment', entityId: enrollment.enrollment_id });
                              setShowRejectModal(true);
                            }}
                            className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600"
                          >
                            Reject
                          </button>
                          <div className="relative">
                            <button
                              onClick={() => toggleActionsMenu(enrollment.enrollment_id)}
                              className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                              title="More actions"
                            >
                              ⋮
                            </button>
                            {showActionsMenu[enrollment.enrollment_id] && (
                              <>
                                <div 
                                  className="fixed inset-0 z-10" 
                                  onClick={() => closeAllMenus()}
                                />
                                <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-20">
                                  <button
                                    onClick={async () => {
                                      closeAllMenus();
                                      if (window.confirm(`Force remove ${enrollment.enrollment_id} from pending list?\n\nOnly use this if the item is stuck and already approved.`)) {
                                        try {
                                          await axiosInstance.post(`/admin/force-remove-pending/kitty_enrollment/${enrollment.enrollment_id}`);
                                          toast.success('Kitty enrollment removed from pending list');
                                          loadPendingApprovals();
                                        } catch (error) {
                                          toast.error(error.response?.data?.detail || 'Failed to remove');
                                        }
                                      }
                                    }}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 text-sm border-b"
                                  >
                                    <span className="text-lg">🔧</span>
                                    <span>Force Remove (if stuck)</span>
                                  </button>
                                  <button
                                    onClick={async () => {
                                      closeAllMenus();
                                      if (window.confirm(`⚠️ PERMANENTLY DELETE ${enrollment.enrollment_id}?\n\nThis will HARD DELETE the record from database.\nOnly use if the item is already processed and stuck.\n\nAre you sure?`)) {
                                        try {
                                          await axiosInstance.delete(`/admin/hard-delete-pending/kitty_enrollment/${enrollment.enrollment_id}`);
                                          toast.success('Kitty enrollment permanently deleted');
                                          loadPendingApprovals();
                                        } catch (error) {
                                          toast.error(error.response?.data?.detail || 'Failed to delete');
                                        }
                                      }
                                    }}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-2 text-sm text-red-600"
                                  >
                                    <span className="text-lg">🗑️</span>
                                    <span>Hard Delete</span>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                          <button
                            onClick={() => navigate(`/kitty/enrollments/${encryptId(enrollment.enrollment_id)}`)}
                            className="py-2 px-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty State for New Orders */}
                {activeTab === 'revisions' && revisionData.total_count === 0 && (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">No pending revisions</p>
                  </div>
                )}
                {activeTab !== 'revisions' && activeTab !== 'approvals' && activeTab !== 'kitty' && (pendingData[activeTab]?.length || 0) === 0 && (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">No pending approvals in this category</p>
                  </div>
                )}
                {activeTab === 'kitty' && (pendingData.kitty_enrollments?.length || 0) === 0 && (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">No pending kitty approvals</p>
                  </div>
                )}
                {activeTab === 'approvals' && (pendingApprovalsList?.length || 0) === 0 && (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">No pending approval requests</p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Reject/Rollback Confirmation Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {selectedEntity?.type === 'provisional' ? 'Rollback Provisional Changes' : 'Reject Submission'}
            </h2>
            <p className="text-gray-600 mb-4">
              {selectedEntity?.type === 'provisional' 
                ? 'Are you sure you want to rollback these provisional changes?' 
                : 'Are you sure you want to reject this submission?'
              }
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedEntity(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={selectedEntity?.type === 'provisional' ? handleRollbackProvisional : handleReject}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold"
              >
                {selectedEntity?.type === 'provisional' ? 'Rollback' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApprovals;
